import { Dialog } from '@spritz-finance/ui'
import { Connector } from '@web3-react/types'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { WALLET_DIALOG_OPENED } from '../../analytics/events'
import { useAnalytics } from '../../context/useAnalytics'
import { useConnectedWallet } from '../ConnectedWalletProvider'
import { getConnection } from '../connection/utils'
import { useWeb3Connection } from '../Web3Provider'
import { WalletConnectContent } from './WalletConnectContent'

type IWalletConnectDialogContext =
    | {
          openWalletConnectDialog: (onConnectProp?: () => void) => void
          closeDialog: () => void
          view: WalletView
          setPendingConnector: React.Dispatch<React.SetStateAction<Connector | undefined>>
          setWalletView: (view: WalletView) => void
          error?: string
          pendingConnector: Connector | undefined
          onConnect: React.MutableRefObject<(() => void) | undefined>
      }
    | undefined

const WalletConnectDialogState = React.createContext<IWalletConnectDialogContext>(undefined)

export const useWalletDialog = () => {
    const context = useContext(WalletConnectDialogState)
    if (!context) throw new Error('Cannot use useWalletDialog outside of a <WalletConnectDialog />')
    return context
}

export enum WalletView {
    Options = 'OPTIONS',
    Account = 'ACCOUNT',
    Connecting = 'CONNECTING',
}

export const WalletConnectDialogProvider: React.FC = ({ children }) => {
    const onConnect = useRef<() => void>()
    const { account } = useWeb3Connection()
    const { updateConnectionError, getErrorByConnector } = useConnectedWallet()
    const { trackAnalyticsEvent } = useAnalytics()
    const [open, setOpen] = useState(false)
    const [view, setView] = useState(account ? WalletView.Account : WalletView.Options)
    const [pendingConnector, setPendingConnector] = useState<Connector | undefined>(undefined)
    const error = pendingConnector ? getErrorByConnector(pendingConnector) : undefined

    useEffect(() => {
        if (open) {
            setView(account ? WalletView.Account : WalletView.Options)
        }
    }, [open, account])

    useEffect(() => {
        if (pendingConnector && view !== WalletView.Connecting) {
            updateConnectionError({
                connectionType: getConnection(pendingConnector).type,
                error: undefined,
            })
            setPendingConnector(undefined)
        }
    }, [pendingConnector, updateConnectionError, view])

    const openDialog = useCallback(
        (onConnectProp?: () => void) => {
            if (onConnectProp) onConnect.current = onConnectProp
            trackAnalyticsEvent(WALLET_DIALOG_OPENED)
            setOpen(true)
        },
        [trackAnalyticsEvent]
    )

    const handleClose = useCallback(() => {
        setOpen(false)
        onConnect.current = undefined
    }, [])

    const handleCloseWithConnect = useCallback(() => {
        setOpen(false)
        if (typeof onConnect.current === 'function') onConnect.current?.()
        onConnect.current = undefined
    }, [])

    const setWalletView = useCallback((view: WalletView) => {
        setView(view)
    }, [])

    return (
        <WalletConnectDialogState.Provider
            value={{
                openWalletConnectDialog: openDialog,
                closeDialog: handleCloseWithConnect,
                view,
                setPendingConnector,
                setWalletView,
                error,
                pendingConnector,
                onConnect,
            }}
        >
            {children}
            <Dialog open={open} onClose={handleClose}>
                <Dialog.Content minWidth={418} style={{ minHeight: 200, padding: 0 }}>
                    <WalletConnectContent />
                </Dialog.Content>
            </Dialog>
        </WalletConnectDialogState.Provider>
    )
}
